import { useNavigate } from "react-router-dom";
import { Container, Row, Col, Card, Badge, Button, Form } from "react-bootstrap";
import { FiAlertCircle, FiPackage, FiClock, FiArrowLeft, FiRefreshCw, FiFilter, FiAlertTriangle, FiXCircle } from "react-icons/fi";
import Useaxios from "utils/Useaxios";
import { API } from "utils/Apiroutes";
import { useEffect, useState } from "react";
import COLORS from "utils/Colors";
import "./Alert.css";

const Alerts = () => {
  const navigate = useNavigate();
  const { fetchData } = Useaxios();
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterType, setFilterType] = useState("ALL");
  const [filterRead, setFilterRead] = useState("ALL");
  const [currentPage, setCurrentPage] = useState(1);

  const itemsPerPage = 10;

  const getAlerts = async () => {
    setLoading(true);
    try {
      const res = await fetchData({
        url: API.stockAlert,
        method: 'GET',
        showMessage: false
      });

      if (res.success) {
        setAlerts(res.data || []);
      }
    } catch (err) {
      console.log(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getAlerts();
  }, []);

  const getAlertIcon = (type) => {
    if (type === "LOW_STOCK") return <FiPackage size={18} />;
    if (type === "EXPIRED") return <FiXCircle size={18} />;
    return <FiAlertTriangle size={18} />;
  };

  const getAlertColor = (type) => {
    if (type === "LOW_STOCK") return "warning";
    if (type === "EXPIRED") return "danger";
    return "info";
  };

  const getAlertLabel = (type) => {
    if (type === "LOW_STOCK") return "Low Stock";
    if (type === "EXPIRED") return "Expired";
    return "Expiry Warning";
  };

  const getAlertClass = (type) => {
    if (type === "EXPIRED") return "alert-expired";
    if (type === "LOW_STOCK") return "alert-low-stock";
    return "alert-warning";
  };

  const markAsRead = async (alertId) => {
    try {
      await fetchData({
        url: API.markAlertRead(alertId),
        method: 'PUT',
        showMessage: false
      });
      setAlerts(prev => prev.map(alert =>
        alert._id === alertId ? { ...alert, isRead: true } : alert
      ));
    } catch (err) {
      console.log(err);
    }
  };

  // Filter alerts
  const filteredAlerts = alerts.filter(alert => {
    if (filterType !== "ALL" && alert.type !== filterType) return false;
    if (filterRead === "READ" && !alert.isRead) return false;
    if (filterRead === "UNREAD" && alert.isRead) return false;
    return true;
  });

  // Pagination
  const totalPages = Math.ceil(filteredAlerts.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedAlerts = filteredAlerts.slice(startIndex, startIndex + itemsPerPage);

  useEffect(() => {
    setCurrentPage(1);
  }, [filterType, filterRead]);

  return (
    <Container fluid className="py-4">
      {/* Header */}
      <Row className="mb-4">
        <Col md="12">
          <Card className="border-0 shadow">
            <Card.Body className="p-4">
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <h2 className="fw-bold mb-1 text-dark">
                    <FiAlertCircle className="me-2" style={{ color: '#dc3545' }} />
                    Product Alerts
                  </h2>
                  <p className="text-muted mb-0">
                    {filteredAlerts.length} alert{filteredAlerts.length !== 1 ? 's' : ''} {filterType !== "ALL" || filterRead !== "ALL" ? "(filtered)" : ""}
                  </p>
                </div>
                <div className="d-flex gap-2">
                  <Button
                    size="sm"
                    onClick={() => navigate('/admin/dashboard')}
                    style={{
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      border: 'none',
                      color: 'white',
                      fontWeight: '500'
                    }}
                  >
                    <FiArrowLeft className="me-2" />
                    Back to Dashboard
                  </Button>
                  <Button
                    size="sm"
                    onClick={getAlerts}
                    disabled={loading}
                    style={{
                      background: loading ? '#6c757d' : 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                      border: 'none',
                      color: 'white',
                      fontWeight: '500'
                    }}
                  >
                    <FiRefreshCw className="me-2" />
                    {loading ? 'Refreshing...' : 'Refresh'}
                  </Button>
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Filters */}
      {!loading && alerts.length > 0 && (
        <Row className="mb-3">
          <Col md="12">
            <Card className="border-0 shadow-sm">
              <Card.Body className="p-3">
                <div className="d-flex gap-3 align-items-center flex-wrap">
                  <div className="d-flex align-items-center gap-2">
                    <FiFilter className="text-muted" size={16} />
                    <span className="text-muted small fw-medium">Filters:</span>
                  </div>
                  <Form.Select
                    size="sm"
                    style={{ width: '200px' }}
                    value={filterType}
                    onChange={(e) => setFilterType(e.target.value)}
                  >
                    <option value="ALL">All Types</option>
                    <option value="EXPIRED">Expired</option>
                    <option value="LOW_STOCK">Low Stock</option>
                  </Form.Select>
                  <Form.Select
                    size="sm"
                    style={{ width: '150px' }}
                    value={filterRead}
                    onChange={(e) => setFilterRead(e.target.value)}
                  >
                    <option value="ALL">All Status</option>
                    <option value="UNREAD">Unread</option>
                    <option value="READ">Read</option>
                  </Form.Select>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      )}

      {/* Alerts List */}
      <Row>
        <Col md="12">
          {loading ? (
            <Card className="border-0 shadow">
              <Card.Body className="text-center py-5">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
                <p className="mt-3 text-muted">Loading alerts...</p>
              </Card.Body>
            </Card>
          ) : filteredAlerts.length === 0 ? (
            <Card className="border-0 shadow">
              <Card.Body className="text-center py-5">
                <div className="mb-3">
                  <FiAlertCircle size={48} className="text-muted" />
                </div>
                <h5 className="fw-bold text-dark mb-2">No Alerts Found</h5>
                <p className="text-muted mb-0">Try adjusting your filters</p>
              </Card.Body>
            </Card>
          ) : (
            <Row className="g-3">
              {paginatedAlerts.map((alert) => (
                <Col lg={6} xl={4} key={alert._id}>
                  <Card
                    className={`border-0 shadow-sm alert-card-hover ${getAlertClass(alert.type)}`}
                    onClick={() => !alert.isRead && markAsRead(alert._id)}
                    style={{
                      cursor: !alert.isRead ? "pointer" : "default",
                      opacity: alert.isRead ? 0.85 : 1,
                      background: alert.type === "EXPIRED" 
                        ? 'linear-gradient(135deg, #ff6b6b 0%, #ee5a6f 100%)'
                        : alert.type === "LOW_STOCK"
                        ? 'linear-gradient(135deg, #ffd93d 0%, #ffb347 100%)'
                        : 'linear-gradient(135deg, #6dd5ed 0%, #2193b0 100%)'
                    }}
                  >
                    <Card.Body className="p-3">
                      <div className="d-flex align-items-start gap-2">
                        <div
                          className="rounded-circle p-2"
                          style={{
                            minWidth: '36px',
                            height: '36px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0,
                            background: 'rgba(255, 255, 255, 0.3)',
                            color: 'white'
                          }}
                        >
                          {getAlertIcon(alert.type)}
                        </div>
                        <div className="flex-grow-1">
                          <div className="d-flex justify-content-between align-items-start mb-1">
                            <Badge bg="light" text="dark" style={{ fontSize: '0.7rem', fontWeight: '600' }}>
                              {getAlertLabel(alert.type)}
                            </Badge>

                            {alert.currentStock !== undefined && (
                              <div className="small fw-bold" style={{ color: 'white' }}>
                                Stock: {alert.currentStock}
                              </div>
                            )}
                            {!alert.isRead && (
                              <Badge bg="danger" pill style={{ fontSize: '0.65rem' }}>
                                New
                              </Badge>
                            )}
                          </div>
                          <div className="fw-bold mb-1" style={{ fontSize: '0.95rem', color: 'white' }}>
                            {alert.productName}
                          </div>
                          <div className="small mb-1" style={{ color: 'rgba(255, 255, 255, 0.9)' }}>
                            {alert.machineId} • Slot {alert.slotNumber}
                          </div>

                          {alert.remainingDays !== undefined && (
                            <div className="small fw-semibold" style={{ color: 'white' }}>
                              Days: {alert.remainingDays}
                            </div>
                          )}
                        </div>
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
              ))}
            </Row>
          )}

          {/* Pagination */}
          {filteredAlerts.length > itemsPerPage && (
            <Row className="mt-4">
              <Col md="12">
                <div className="d-flex justify-content-center gap-2">
                  <Button
                    variant="outline-primary"
                    size="sm"
                    disabled={currentPage === 1}
                    onClick={() => setCurrentPage(prev => prev - 1)}
                  >
                    Previous
                  </Button>
                  <span className="d-flex align-items-center px-3">
                    Page {currentPage} of {totalPages}
                  </span>
                  <Button
                    variant="outline-primary"
                    size="sm"
                    disabled={currentPage === totalPages}
                    onClick={() => setCurrentPage(prev => prev + 1)}
                  >
                    Next
                  </Button>
                </div>
              </Col>
            </Row>
          )}
        </Col>
      </Row>


    </Container>
  );
};

export default Alerts;
